import { type AxiosPromise, type AxiosRequestConfig } from "axios";

import { track } from "shared/boot/analytics";
import ApiClient from "shared/services/api/ApiClient";
import { type Nullable } from "shared/types";
import { type Mention } from "shared/types/mentions";

import {
  type OrganisationReport,
  type OrganisationReportServiceOptions,
  type OrganisationReportServicePreviewParams,
  type OrganisationReportServiceSavedPreviewParams,
  type OrganisationReportServiceSummaryCountResponse,
  type OrganisationReportServiceSummaryResponse,
  type OrganisationReportServiceUpdateParams,
  type OrganisationReportSummaryType,
} from "../types";

class OrganisationReportService extends ApiClient {
  constructor() {
    super("/organisation_reports");
  }

  // API calls

  aggregateAnalytics(options: AxiosRequestConfig = {}) {
    return this.get("/analytics", options);
  }

  analytics(id: number | string, options: AxiosRequestConfig = {}) {
    return this.get(`/${id}/analytics`, options);
  }

  create(options: AxiosRequestConfig = {}) {
    track("Created Organisation Report");

    return super.create(options);
  }

  decrementSummaryCount(
    decrementBy = 1
  ): AxiosPromise<OrganisationReportServiceSummaryCountResponse> {
    return this.put("/decrement_summary_count", {
      params: {
        decrement_by: decrementBy,
      },
    });
  }

  destroy(reportId: number | string, options: AxiosRequestConfig = {}) {
    track("Deleted Organisation Report");

    return super.destroy(reportId, options);
  }

  duplicateReport(reportId: number | string) {
    track("Duplicating Organisation Report");

    return this.post(`/${reportId}/duplicate`);
  }

  email(id: number | string, options: AxiosRequestConfig = {}) {
    track("Sent Organisation Report");

    return this.post(`/${id}/email`, options);
  }

  mentionsSummary(
    options: AxiosRequestConfig = {},
    reportId: Nullable<number | string> = null
  ): AxiosPromise<OrganisationReportServiceSummaryResponse> {
    const pathPrefix = reportId ? `/${reportId}` : "";

    return this.post(`${pathPrefix}/mentions_summary`, options);
  }

  // Utility Methods

  mentionSummary(
    mention: Mention,
    summaryType: OrganisationReportSummaryType,
    isDerBuilder: boolean = false,
    specificationId: Nullable<number> = null
  ): AxiosPromise<OrganisationReportServiceSummaryResponse> {
    return this.get(`/mention_summary`, {
      params: {
        is_der_builder: isDerBuilder,
        keywords: Object.keys(mention.keywords ?? {}).map((keywords) =>
          keywords.replace(/<[^>]*>?/gm, "")
        ),
        specification_id: specificationId,
        summary_type: summaryType,
        target_id: mention.id,
        target_type: mention.type,
      },
    });
  }

  preview(
    options: OrganisationReportServiceOptions<OrganisationReportServicePreviewParams>
  ) {
    return this.post("/preview", options);
  }

  savedPreview(
    id: number | string,
    options: OrganisationReportServiceOptions<OrganisationReportServiceSavedPreviewParams>
  ): AxiosPromise<string> {
    return this.get(`/${id}/preview`, options);
  }

  summaryCount(
    increment: boolean = false
  ): AxiosPromise<OrganisationReportServiceSummaryCountResponse> {
    return this.get("/summary_count", {
      params: {
        increment_count: increment,
      },
    });
  }

  testEmail(id: number | string, options: AxiosRequestConfig = {}) {
    return this.post(`/${id}/test_email`, options);
  }

  update<T = OrganisationReport>(
    reportId: number | string,
    options?: OrganisationReportServiceOptions<OrganisationReportServiceUpdateParams>
  ): AxiosPromise<T> {
    track("Updated Organisation Report");

    return super.update<T>(reportId, options);
  }
}

export default new OrganisationReportService();
